/**
 * 다국어 리소스를 사용하기 위한 커스텀 훅
 *
 * @example
 * ```tsx
 * import { useLocale } from '../hooks/use-locale';
 * import { t } from '../common/i18n';
 *
 * function MyComponent() {
 *   const { triggerStr, commonStr, setLocale } = useLocale();
 *
 *   // 단순 메시지
 *   const message = t(triggerStr.message.strategyUpdated, { name: 'MyStrategy' });
 *
 *   // 언어 변경
 *   const handleLanguageChange = () => {
 *     setLocale('en');
 *   };
 *
 *   return <div>{message}</div>;
 * }
 * ```
 */

import { useContext } from 'react';
import { LocaleContext } from '../contexts/locale-context';
import type { LocaleConfig } from '../locales/types';

/**
 * 다국어 리소스와 언어 설정 함수를 반환하는 훅
 *
 * @returns LocaleConfig - 현재 언어, 리소스, 언어 변경 함수
 * @throws LocaleProvider로 감싸지 않은 경우 에러 발생
 */
export function useLocale(): LocaleConfig {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }

  return context;
}

/**
 * 편의를 위한 개별 리소스 반환 함수들
 */
export function useLocaleResources() {
  const { resources } = useLocale();
  return resources;
}

export function useCurrentLocale() {
  const { currentLocale } = useLocale();
  return currentLocale;
}

export function useSetLocale() {
  const { setLocale } = useLocale();
  return setLocale;
}
